import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MEASUREMENT_UNITS, translateMeasurementUnitLabel } from '@/types/measurement';
import type { MeasurementUnit } from '@/types/measurement';
import type {
  AdminWorkspaceCurrencyCode,
  AdminWorkspaceItemState,
  AdminWorkspaceMode,
  AdminWorkspaceOrderState,
} from './types';
import { ORDER_ITEM_STATUSES } from '@/types/orderStatus';
import {
  PAID_UI_ITEM_MEMBER_STATUSES,
  PAID_UI_ITEM_STATUS,
  getItemStatusLabel,
  isPaidUiItemStatus,
} from './types';

interface WorkspaceToolbarProps {
  onResetFilters: () => void;
  mode: AdminWorkspaceMode;
  onModeChange: (mode: AdminWorkspaceMode) => void;
  measurementUnit: MeasurementUnit;
  onMeasurementUnitChange: (unit: MeasurementUnit) => void;
  currencyCode: AdminWorkspaceCurrencyCode;
  onCurrencyCodeChange: (currencyCode: AdminWorkspaceCurrencyCode) => void;
  itemState: AdminWorkspaceItemState;
  orderState: AdminWorkspaceOrderState;
  onRefreshItems: () => void;
  onRefreshOrders: () => void;
  onItemGroupModeChange: (mode: 'paperType' | 'order') => void;
  onItemSearchInputChange: (value: string) => void;
  onItemDateFromChange: (value: string) => void;
  onItemDateToChange: (value: string) => void;
  onItemOnlyFirstOrderChange: (value: boolean) => void;
  onToggleItemStatusFilter: (status: (typeof ORDER_ITEM_STATUSES)[number]) => void;
  onExportAllItems: () => void;
  onOrderSearchInputChange: (value: string) => void;
  onOrderDateFromChange: (value: string) => void;
  onOrderDateToChange: (value: string) => void;
  onToggleOrderStatusVisibility: (status: string) => void;
}

interface StatusOption {
  value: string;
  label: string;
}

interface StatusFilterDropdownProps {
  menuLabel: string;
  allLabel: string;
  emptySelectionLabel: string;
  options: ReadonlyArray<StatusOption>;
  selectedStatuses: Set<string>;
  onApplySelection: (nextSelectedStatuses: Set<string>) => void;
}

const pillClass = (active: boolean): string => (
  `rounded-full px-3 py-1 text-xs font-semibold transition ${active ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:bg-muted/60'}`
);

const formatDateInputDisplay = (value: string): string => value.replace(/-/g, '/');

const normalizeDateInputDisplay = (value: string): string => value.replace(/\//g, '-');

const EXPORTABLE_ITEM_STATUSES = new Set([
  'FIRST_ORDER',
  'SECOND_ORDER',
  'PAID',
  'REUPLOAD_IMAGE',
  'REUPLOADED',
  'READY_TO_PRINT',
]);

const buildExportAllOutputDescription = (
  t: ReturnType<typeof useTranslation>['t'],
  hasMetadataOnlyCandidates: boolean,
): string => hasMetadataOnlyCandidates
  ? t(
      'adminOrdersWorkspace.confirmations.exportAllOutputMixed',
      'Output: ZIP archive of print-ready exports. Files without margin or gallery wrap may keep the original JPEG, PNG, or TIFF format with updated DPI metadata; files that need processing are exported as TIFF.',
    )
  : t(
      'adminOrdersWorkspace.confirmations.exportOutputZip',
      'Output: ZIP archive with TIFF print files.',
    );

const buildSelectedStatusesText = (
  selectedStatuses: Set<string>,
  options: ReadonlyArray<StatusOption>,
  emptyLabel: string,
): string => {
  const selectedLabels = options
    .filter((option) => selectedStatuses.has(option.value))
    .map((option) => option.label);

  if (selectedLabels.length === 0) {
    return emptyLabel;
  }

  return selectedLabels.join(', ');
};

const StatusFilterDropdown = ({
  menuLabel,
  allLabel,
  emptySelectionLabel,
  options,
  selectedStatuses,
  onApplySelection,
}: StatusFilterDropdownProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pendingStatuses, setPendingStatuses] = useState<Set<string>>(new Set(selectedStatuses));
  const ignoreNextItemSelectRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPendingStatuses(new Set(selectedStatuses));
    }
  }, [open, selectedStatuses]);

  const selectedStatusesText = buildSelectedStatusesText(selectedStatuses, options, emptySelectionLabel);
  const allSelected = pendingStatuses.size === options.length && options.length > 0;

  const togglePendingStatus = (status: string) => {
    setPendingStatuses((currentStatuses) => {
      const nextStatuses = new Set(currentStatuses);

      if (nextStatuses.has(status)) {
        nextStatuses.delete(status);
      } else {
        nextStatuses.add(status);
      }

      onApplySelection(new Set(nextStatuses));
      return nextStatuses;
    });
  };

  const handleToggleAllPendingStatuses = () => {
    const nextStatuses = allSelected ? new Set<string>() : new Set(options.map((option) => option.value));
    setPendingStatuses(nextStatuses);
    onApplySelection(new Set(nextStatuses));
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setPendingStatuses(new Set(selectedStatuses));
    }

    setOpen(nextOpen);
  };

  const handleSingleSelect = (status: string) => {
    const nextStatuses = status === '__all__'
      ? new Set(options.map((option) => option.value))
      : new Set([status]);

    setPendingStatuses(nextStatuses);
    onApplySelection(nextStatuses);
    setOpen(false);
  };

  const handleItemSelect = (event: Event, status: string) => {
    if (ignoreNextItemSelectRef.current) {
      ignoreNextItemSelectRef.current = false;
      event.preventDefault();
      return;
    }

    handleSingleSelect(status);
  };

  const handleCheckboxPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    ignoreNextItemSelectRef.current = true;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCheckboxClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    onToggle: () => void,
  ) => {
    ignoreNextItemSelectRef.current = true;
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="max-w-[18rem] justify-start overflow-hidden">
                <span className="truncate">{selectedStatusesText}</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={(event) => handleItemSelect(event, '__all__')} className="gap-2">
              <button
                type="button"
                aria-label={t('adminOrdersWorkspace.toolbar.toggleStatus', 'Toggle {{label}}', { label: allLabel })}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary"
                onPointerDown={handleCheckboxPointerDown}
                onClick={(event) => handleCheckboxClick(event, handleToggleAllPendingStatuses)}
              >
                {allSelected ? <Check className="h-3.5 w-3.5" /> : null}
              </button>
              <span>{allLabel}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {options.map((option) => {
              const checked = pendingStatuses.has(option.value);

              return (
                <DropdownMenuItem key={option.value} onSelect={(event) => handleItemSelect(event, option.value)} className="gap-2">
                  <button
                    type="button"
                    aria-label={t('adminOrdersWorkspace.toolbar.toggleStatus', 'Toggle {{label}}', { label: option.label })}
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary"
                    onPointerDown={handleCheckboxPointerDown}
                    onClick={(event) => handleCheckboxClick(event, () => togglePendingStatus(option.value))}
                  >
                    {checked ? <Check className="h-3.5 w-3.5" /> : null}
                  </button>
                  <span>{option.label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipContent className="max-w-sm break-words">
          {selectedStatusesText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const WorkspaceToolbar = ({
  mode,
  onModeChange,
  measurementUnit,
  onMeasurementUnitChange,
  currencyCode,
  onCurrencyCodeChange,
  itemState,
  orderState,
  onRefreshItems,
  onRefreshOrders,
  onItemGroupModeChange,
  onItemSearchInputChange,
  onItemDateFromChange,
  onItemDateToChange,
  onItemOnlyFirstOrderChange,
  onToggleItemStatusFilter,
  onExportAllItems,
  onOrderSearchInputChange,
  onOrderDateFromChange,
  onOrderDateToChange,
  onToggleOrderStatusVisibility,
  onResetFilters,
}: WorkspaceToolbarProps) => {
  const { t } = useTranslation();
  const [confirmExportAllOpen, setConfirmExportAllOpen] = useState(false);
  const isItemsMode = mode === 'items';
  const activeStatus = isItemsMode ? itemState.status : orderState.status;
  const activeError = isItemsMode ? itemState.error : orderState.error;
  const activeSearchInput = isItemsMode ? itemState.searchInput : orderState.searchInput;
  const unitLabels = useMemo(
    () => Object.fromEntries(MEASUREMENT_UNITS.map((unit) => [unit, translateMeasurementUnitLabel(t, unit)])),
    [t],
  );
  const itemStatusOptions = useMemo<ReadonlyArray<StatusOption>>(() => ([
    'NEW',
    'IN_ORDER',
    PAID_UI_ITEM_STATUS,
    'REUPLOAD_IMAGE',
    'REUPLOADED',
    'READY_TO_PRINT',
    'PRINTED',
    'IN_DELIVERY',
    'DELIVERED',
    'RETURNED',
    'CANCELLED',
  ] as const).map((statusOption) => ({
    value: statusOption,
    label: statusOption === PAID_UI_ITEM_STATUS ? getItemStatusLabel(t, 'PAID') : getItemStatusLabel(t, statusOption),
  })), [t]);
  const orderStatusOptions = useMemo(
    () => orderState.statusOptions.map((statusKey) => ({ value: statusKey, label: t(`orders.status.${statusKey}`, statusKey) })),
    [orderState.statusOptions, t],
  );
  const selectedItemStatuses = useMemo(() => {
    const nextStatuses = new Set<string>();

    ORDER_ITEM_STATUSES.forEach((statusOption) => {
      if (isPaidUiItemStatus(statusOption)) {
        return;
      }

      if (itemState.statusFilters.has(statusOption)) {
        nextStatuses.add(statusOption);
      }
    });

    if (PAID_UI_ITEM_MEMBER_STATUSES.every((statusOption) => itemState.statusFilters.has(statusOption))) {
      nextStatuses.add(PAID_UI_ITEM_STATUS);
    }

    return nextStatuses;
  }, [itemState.statusFilters]);
  const eligibleExportItems = useMemo(
    () => itemState.filteredItems.filter((item) => EXPORTABLE_ITEM_STATUSES.has(item.status)),
    [itemState.filteredItems],
  );
  const eligibleExportItemsCount = eligibleExportItems.length;
  const exportAllHasMetadataOnlyCandidates = useMemo(
    () => eligibleExportItems.some((item) => (item.marginIn ?? 0) <= 0 && (item.galleryWrapIn ?? 0) <= 0),
    [eligibleExportItems],
  );

  const applyItemStatusSelection = (nextSelectedStatuses: Set<string>) => {
    const nextRawStatuses = new Set<(typeof ORDER_ITEM_STATUSES)[number]>();

    nextSelectedStatuses.forEach((statusOption) => {
      if (statusOption === PAID_UI_ITEM_STATUS) {
        PAID_UI_ITEM_MEMBER_STATUSES.forEach((memberStatus) => nextRawStatuses.add(memberStatus));
        return;
      }

      nextRawStatuses.add(statusOption as (typeof ORDER_ITEM_STATUSES)[number]);
    });

    ORDER_ITEM_STATUSES.forEach((statusOption) => {
      const isCurrentlySelected = itemState.statusFilters.has(statusOption);
      const shouldBeSelected = nextRawStatuses.has(statusOption);

      if (isCurrentlySelected !== shouldBeSelected) {
        onToggleItemStatusFilter(statusOption);
      }
    });
  };

  const applyOrderStatusSelection = (nextSelectedStatuses: Set<string>) => {
    orderState.statusOptions.forEach((statusKey) => {
      const isCurrentlySelected = orderState.visibleStatuses.has(statusKey);
      const shouldBeSelected = nextSelectedStatuses.has(statusKey);

      if (isCurrentlySelected !== shouldBeSelected) {
        onToggleOrderStatusVisibility(statusKey);
      }
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t('adminOrdersWorkspace.toolbar.title', 'Orders workspace')}</CardTitle>
            <CardDescription>
              {t('adminOrdersWorkspace.toolbar.description', 'Switch between order-level operations and item-level print preparation in one screen.')}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex flex-wrap items-end justify-end gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.mode', 'Mode')}</span>
                <div className="inline-flex rounded-full border border-border/70 bg-muted/20 p-1">
                  <button type="button" className={pillClass(mode === 'orders')} onClick={() => onModeChange('orders')} aria-pressed={mode === 'orders'}>
                    {t('adminOrdersWorkspace.toolbar.orders', 'Orders')}
                  </button>
                  <button type="button" className={pillClass(mode === 'items')} onClick={() => onModeChange('items')} aria-pressed={mode === 'items'}>
                    {t('adminOrdersWorkspace.toolbar.items', 'Items')}
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.units', 'Units')}</span>
                <div className="inline-flex rounded-full border border-border/70 bg-muted/20 p-1">
                  {MEASUREMENT_UNITS.map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      className={pillClass(unit === measurementUnit)}
                      onClick={() => onMeasurementUnitChange(unit)}
                      aria-pressed={unit === measurementUnit}
                    >
                      {unitLabels[unit]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.currency', 'Currency')}</span>
                <div className="inline-flex rounded-full border border-border/70 bg-muted/20 p-1">
                  {(['USD', 'UAH'] as const).map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={pillClass(code === currencyCode)}
                      onClick={() => onCurrencyCodeChange(code)}
                      aria-pressed={code === currencyCode}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {isItemsMode ? (
              <div className="flex flex-wrap items-end justify-end gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.groupBy', 'Group by')}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant={itemState.groupMode === 'paperType' ? 'default' : 'outline'} size="sm" onClick={() => onItemGroupModeChange('paperType')}>
                      {t('adminOrdersWorkspace.toolbar.paperType', 'Paper type')}
                    </Button>
                    <Button type="button" variant={itemState.groupMode === 'order' ? 'default' : 'outline'} size="sm" onClick={() => onItemGroupModeChange('order')}>
                      {t('adminOrdersWorkspace.toolbar.orderNumber', 'Order number')}
                    </Button>
                  </div>
                </div>

              </div>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.search', 'Search')}</span>
            <div className="relative w-[280px]">
              <Input
                type="text"
                value={activeSearchInput}
                onChange={(event) => (isItemsMode ? onItemSearchInputChange(event.target.value) : onOrderSearchInputChange(event.target.value))}
                placeholder={t('adminOrdersWorkspace.toolbar.searchPlaceholder', 'Display code, customer, address')}
                className="w-full pr-10"
              />
              {activeSearchInput.length > 0 ? (
                <button
                  type="button"
                  onClick={() => (isItemsMode ? onItemSearchInputChange('') : onOrderSearchInputChange(''))}
                  aria-label={t('adminOrdersWorkspace.toolbar.clearSearch', 'Clear search')}
                  className="absolute inset-y-0 right-1 flex w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.status', 'Status')}</span>
            <StatusFilterDropdown
              menuLabel={isItemsMode
                ? t('adminOrdersWorkspace.toolbar.itemStatuses', 'Item statuses')
                : t('adminOrdersWorkspace.toolbar.orderStatuses', 'Order statuses')}
              allLabel={t('adminOrdersWorkspace.toolbar.all', 'All')}
              emptySelectionLabel={t('adminOrdersWorkspace.toolbar.noStatuses', 'No statuses')}
              options={isItemsMode ? itemStatusOptions : orderStatusOptions}
              selectedStatuses={isItemsMode ? selectedItemStatuses : new Set(orderState.visibleStatuses)}
              onApplySelection={isItemsMode ? applyItemStatusSelection : applyOrderStatusSelection}
            />
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.from', 'From')}</span>
              <div className="relative w-[172px]">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatDateInputDisplay(isItemsMode ? itemState.dateFrom : orderState.dateFrom)}
                  onChange={(event) => (isItemsMode
                    ? onItemDateFromChange(normalizeDateInputDisplay(event.target.value))
                    : onOrderDateFromChange(normalizeDateInputDisplay(event.target.value)))}
                  placeholder={t('adminOrdersWorkspace.toolbar.datePlaceholder', 'YYYY/MM/DD')}
                  className="w-full pr-10"
                />
                <div className="pointer-events-none absolute inset-y-0 right-1 flex w-9 items-center justify-center text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                </div>
                <input
                  type="date"
                  tabIndex={-1}
                  aria-label={t('adminOrdersWorkspace.toolbar.selectFromDate', 'Select from date')}
                  value={isItemsMode ? itemState.dateFrom : orderState.dateFrom}
                  onChange={(event) => (isItemsMode
                    ? onItemDateFromChange(event.target.value)
                    : onOrderDateFromChange(event.target.value))}
                  className="absolute inset-y-0 right-1 w-9 cursor-pointer opacity-0"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">{t('adminOrdersWorkspace.toolbar.to', 'To')}</span>
              <div className="relative w-[172px]">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatDateInputDisplay(isItemsMode ? itemState.dateTo : orderState.dateTo)}
                  onChange={(event) => (isItemsMode
                    ? onItemDateToChange(normalizeDateInputDisplay(event.target.value))
                    : onOrderDateToChange(normalizeDateInputDisplay(event.target.value)))}
                  placeholder={t('adminOrdersWorkspace.toolbar.datePlaceholder', 'YYYY/MM/DD')}
                  className="w-full pr-10"
                />
                <div className="pointer-events-none absolute inset-y-0 right-1 flex w-9 items-center justify-center text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                </div>
                <input
                  type="date"
                  tabIndex={-1}
                  aria-label={t('adminOrdersWorkspace.toolbar.selectToDate', 'Select to date')}
                  value={isItemsMode ? itemState.dateTo : orderState.dateTo}
                  onChange={(event) => (isItemsMode
                    ? onItemDateToChange(event.target.value)
                    : onOrderDateToChange(event.target.value))}
                  className="absolute inset-y-0 right-1 w-9 cursor-pointer opacity-0"
                />
              </div>
            </div>
            {isItemsMode ? (
              <label className="flex items-center gap-2 pb-2 text-sm font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={itemState.onlyFirstOrder}
                  onChange={(event) => onItemOnlyFirstOrderChange(event.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <span>{t('adminOrdersWorkspace.toolbar.onlyFirstOrder', 'Only 1st order')}</span>
              </label>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={onResetFilters}>{t('adminOrdersWorkspace.actions.resetFilters', 'Reset filters')}</Button>
          <Button onClick={isItemsMode ? onRefreshItems : onRefreshOrders} disabled={activeStatus === 'loading'}>
            {activeStatus === 'loading' ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('adminOrdersWorkspace.actions.loading', 'Loading')}
              </span>
            ) : t('adminOrdersWorkspace.actions.refresh', 'Refresh')}
          </Button>
          {activeError ? <p className="text-sm text-destructive">{activeError}</p> : null}
          {!activeError && activeStatus === 'idle' ? (
            isItemsMode ? (
              <p className="text-sm text-muted-foreground">
                {t('adminOrdersWorkspace.toolbar.itemsShown', '{{filtered}} of {{total}} items shown', { filtered: itemState.filteredItems.length, total: itemState.items.length })}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('adminOrdersWorkspace.toolbar.ordersLoaded', '{{count}} orders loaded', { count: orderState.orders.length })}
              </p>
            )
          ) : null}
        </div>

        {isItemsMode ? (
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => setConfirmExportAllOpen(true)}
              disabled={itemState.isExportingAll || itemState.status === 'loading' || eligibleExportItemsCount === 0}
            >
              {itemState.isExportingAll ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('adminOrdersWorkspace.actions.exporting', 'Exporting')}
                </span>
              ) : t('adminOrdersWorkspace.actions.exportAll', 'Export all')}
            </Button>
          </div>
        ) : null}

        <ConfirmAlertDialog
          open={confirmExportAllOpen}
          onOpenChange={setConfirmExportAllOpen}
          title={t('adminOrdersWorkspace.confirmations.exportAllTitle', 'Confirm export all')}
          description={(
            <div className="space-y-2">
              <p>{t('adminOrdersWorkspace.confirmations.exportMode', 'Export mode')}: {t('adminOrdersWorkspace.confirmations.exportAllPerGroupMode', 'Each group uses its selected export mode')}</p>
              <p>{t('adminOrdersWorkspace.exportModes.auto.description', 'Uses one shared resolution for the whole group based on the highest effective print PPI in that group.')}</p>
              <p>{t('adminOrdersWorkspace.confirmations.filesIncluded', 'Files included')}: {eligibleExportItemsCount} / {itemState.filteredItems.length}</p>
              <p>{buildExportAllOutputDescription(t, exportAllHasMetadataOnlyCandidates)}</p>
            </div>
          )}
          confirmText={t('adminOrdersWorkspace.confirmations.exportButton', 'Export')}
          cancelText={t('adminOrdersWorkspace.confirmations.cancelButton', 'Cancel')}
          onConfirm={() => {
            setConfirmExportAllOpen(false);
            onExportAllItems();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default WorkspaceToolbar;
